import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TRANSLATIONS } from "@/i18n/translations";

// Localized URL paths. Keys match across both languages.
export const ROUTES = {
  en: {
    home: "/",
    about: "/about",
    team: "/team",
    services: "/services",
    projects: "/projects",
    exhibitions: "/exhibitions",
    venues: "/venues",
    faq: "/faq",
    contact: "/contact",
    imprint: "/imprint",
    privacy: "/privacy",
  },
  de: {
    home: "/de",
    about: "/de/ueber-uns",
    team: "/de/team",
    services: "/de/leistungen",
    projects: "/de/konzepte",
    exhibitions: "/de/ausstellungen",
    venues: "/de/locations",
    faq: "/de/faq",
    contact: "/de/kontakt",
    imprint: "/de/impressum",
    privacy: "/de/datenschutz",
  },
};

function detectLangFromPath(pathname) {
  return pathname === "/de" || pathname.startsWith("/de/") ? "de" : "en";
}

function findRouteKey(pathname) {
  const clean = pathname.replace(/\/+$/, "") || "/";
  for (const lang of ["en", "de"]) {
    for (const key in ROUTES[lang]) {
      const route = ROUTES[lang][key].replace(/\/+$/, "") || "/";
      if (route === clean) return key;
    }
  }
  return "home";
}

const LanguageContext = createContext({
  lang: "en",
  setLang: () => {},
});

/**
 * LanguageProvider MUST be rendered inside <BrowserRouter>.
 * It keeps the active language in sync with the URL:
 *   - On any route change, lang is derived from the pathname
 *   - setLang(newLang) navigates to the equivalent localized URL
 */
export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLangState] = useState(() => detectLangFromPath(location.pathname));

  // German is the default language: on first visit (no stored preference),
  // redirect English URLs to their German equivalent.
  useEffect(() => {
    const stored = localStorage.getItem("preferredLang");
    if (!stored && detectLangFromPath(location.pathname) === "en") {
      const key = findRouteKey(location.pathname);
      navigate(ROUTES.de[key] || ROUTES.de.home, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync lang -> from URL
  useEffect(() => {
    const next = detectLangFromPath(location.pathname);
    if (next !== lang) setLangState(next);
    // Reflect on <html lang> for SEO/accessibility
    if (typeof document !== "undefined") {
      document.documentElement.lang = next;
    }
  }, [location.pathname, lang]);

  const setLang = useCallback(
    (newLang) => {
      localStorage.setItem("preferredLang", newLang);
      if (newLang === lang) return;
      const key = findRouteKey(location.pathname);
      const target = ROUTES[newLang]?.[key] || ROUTES[newLang]?.home || "/";
      navigate(target);
    },
    [lang, location.pathname, navigate]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>{children}</LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}

export function useT() {
  const { lang } = useContext(LanguageContext);
  return TRANSLATIONS[lang] || TRANSLATIONS.en;
}

/** Returns a function that maps a route key (e.g. "exhibitions") to a localized URL. */
export function useRoute() {
  const { lang } = useContext(LanguageContext);
  return (key) => ROUTES[lang][key] || ROUTES.en[key] || "/";
}

/** Returns the equivalent path in the opposite language (used for hreflang alternates). */
export function useAlternatePath() {
  const { lang } = useContext(LanguageContext);
  const location = useLocation();
  const other = lang === "en" ? "de" : "en";
  const key = findRouteKey(location.pathname);
  return { lang: other, path: ROUTES[other][key] || ROUTES[other].home };
}

// Translate exhibition schedule + hours strings to the active language.
const DE_REPLACEMENTS = [
  [/\bStarting September 11\b/g, "Ab 11. September"],
  [/\bStarting October 22\b/g, "Ab 22. Oktober"],
  [/\bPermanently\b/g, "Permanent"],
  [/\bUntil\b/g, "Bis"],
  [/\bOpen Daily\b/gi, "Täglich geöffnet"],
  [/\bMon\b/g, "Mo"],
  [/\bTue\b/g, "Di"],
  [/\bWed\b/g, "Mi"],
  [/\bThu\b/g, "Do"],
  [/\bFri\b/g, "Fr"],
  [/\bSat\b/g, "Sa"],
  [/\bSun\b/g, "So"],
  [/\bHolidays\b/g, "Feiertage"],
];

export function useScheduleTranslator() {
  const { lang } = useContext(LanguageContext);
  return (text) => {
    if (!text || lang !== "de") return text;
    return DE_REPLACEMENTS.reduce((s, [re, rep]) => s.replace(re, rep), text);
  };
}
